import React, { useState, useEffect } from "react";
import {
  Button,
  Flex,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Box,
  useColorModeValue,
  Text,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useBreakpointValue,
  Divider,
} from "@chakra-ui/react";
import { ToastContainer, toast } from "react-toastify";
import { FaTrash, FaExclamationCircle, FaInfoCircle, FaBook } from "react-icons/fa";
import { useStore } from "@nanostores/react";
import { userProfile } from "contexts/userProfile";
import { useLocation, useHistory } from "react-router-dom";
import { storage, db } from "lib/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  collection,
  setDoc,
  doc,
  serverTimestamp,
  query,
  where,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import Lottie from "lottie-react";
import addAnimation from "assets/animations/add.json";

function AddSigner() {
  const { data, auth } = useStore(userProfile);
  const { uid, displayName, email } = data;

  const [documentName, setDocumentName] = useState("");
  const [currentSigner, setCurrentSigner] = useState({ name: "", email: "" });
  const [addedSigners, setAddedSigners] = useState([]);
  const [insightsData, setInsightsData] = useState({});
  const [isInsightsModalOpen, setInsightsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [buttonPressed, setButtonPressed] = useState(false);

  const location = useLocation();
  const history = useHistory();

  const selectedDocument = location.state?.selectedDocument || location.state?.pdfBlob;

  if (!selectedDocument) {
    history.push("/admin/dashboard");
  }
  let fileName;

  const handleSignerInputChange = (field) => (e) => {
    setCurrentSigner((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const addSigner = async () => {
    if (!currentSigner.name.trim() || !currentSigner.email.trim()) {
      toast.error("Both name and email fields should be filled!");
      return;
    }

    if (!currentSigner.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    const userUID = await getUIDFromEmail(currentSigner.email);
    if (!userUID) {
      toast.error("The provided email does not have an account.");
      return;
    }

    setAddedSigners((prev) => [...prev, currentSigner]);
    setCurrentSigner({ name: "", email: "" });
  };

  const deleteSigner = (index) => {
    setAddedSigners((prev) => prev.filter((_, i) => i !== index));
  };

  const handleBackClick = () => {
    history.push("/admin/dashboard");
  };

  const saveSignatureToFirestore = async (response) => {
    const signatureRequestId = response.data.signatureRequest.signatureRequestId;

    if (!signatureRequestId) {
      console.error("No signatureRequestId found in the response.");
      return;
    }

    const signaturesRef = collection(db, "signatures");
    const signatureDocRef = doc(signaturesRef, signatureRequestId);

    const res = response?.data?.signatureRequest;

    if (!res) {
      console.error("Unexpected response structure:", response?.data);
      toast.error("Failed to save signature data due to unexpected server response.");
      return;
    }

    const insightsData = await fetchInsights(fileName);

    const transformedKeyTakeaways = insightsData.keyTakeaways
      ? Object.fromEntries(
          insightsData.keyTakeaways.map((takeaway, index) => [`takeaway${index + 1}`, takeaway])
        )
      : {};

    const transformedRiskyClauses = insightsData.riskyClauses
      ? Object.fromEntries(
          insightsData.riskyClauses.map((clause, index) => [`clause${index + 1}`, clause])
        )
      : {};

    const insights = {
      keyTakeaways: transformedKeyTakeaways,
      riskyClauses: transformedRiskyClauses,
      summary: {
        summaryText: insightsData.summary,
      },
    };

    const signatureData = {
      createdAt: serverTimestamp(),
      createdBy: uid,
      detailsUrl: res?.detailsUrl,
      filesUrl: res?.filesUrl,
      documentName: documentName,
      insights: insights,
      signers: Object.fromEntries(
        res?.signatures.map((signer, index) => [
          `Signer${index + 1}`,
          {
            signatureId: signer.signatureId,
            signerEmailAddress: signer.signerEmailAddress,
            signerName: signer.signerName,
            pendingReview: false,
            order: signer.order,
            statusCode: signer.statusCode,
            signedAt: signer.signedAt,
          },
        ])
      ),
    };

    try {
      await setDoc(signatureDocRef, signatureData);
      const signerEmails = addedSigners.map((signer) => signer.email);
      await saveSignatureReferenceToUsers(uid, signerEmails, signatureRequestId);
    } catch (error) {
      console.error("Error storing signature: ", error);
      toast.error("Failed to save signature data.");
    }
  };

  async function getUIDFromEmail(email) {
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", email));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      return querySnapshot.docs[0].id;
    }
    return null;
  }

  const saveSignatureReferenceToUsers = async (ownerUID, signerEmails, signatureRequestId) => {
    const usersRef = collection(db, "users");
    const batch = writeBatch(db);

    // Save for the owner
    const ownerRef = doc(usersRef, ownerUID, "signatureRequests", signatureRequestId);
    batch.set(ownerRef, { signatureRequestId: signatureRequestId });

    // Save for each signer
    for (let email of signerEmails) {
      const signerUID = await getUIDFromEmail(email);
      if (signerUID) {
        const signerRef = doc(usersRef, signerUID, "signatureRequests", signatureRequestId);
        batch.set(signerRef, {});
      }
    }

    await batch.commit();
  };

  const handleNextClick = async () => {
    setLoading(true);
    if (!documentName || addedSigners.length === 0) {
      toast.error("Please fill in all the fields before proceeding.");
      return;
    }
    if (!selectedDocument) {
      toast.error("No document selected!");
      return;
    }

    const userId = uid;
    if (!userId) {
      toast.error("No user ID found. Cannot save to Firebase.");
      return;
    }

    console.log(selectedDocument);

    fileName = `${selectedDocument.name.split(".")[0]}_${Date.now()}.pdf`;
    console.log(fileName);
    const uniquePath = `documents/${userId}/${fileName}`;
    const storageRef = ref(storage, uniquePath);

    const signerEmails = addedSigners.map((signer) => signer.email);
    // const ownerEmail = email;

    // if (ownerEmail) {
    //   signerEmails.push(ownerEmail);
    // }

    try {
      await uploadBytes(storageRef, await selectedDocument.arrayBuffer());
      const firebaseUrl = await getDownloadURL(storageRef);

      const response = await fetch("https://api-seyimfnmxq-uc.a.run.app/createEmbeddedRequest", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: "CogniSigner - Document Signature Request",
          title:
            "Please sign this Document and then we can discuss more. Let me know if you have any questions",
          signers: addedSigners.map((signer, index) => ({
            emailAddress: signer.email,
            name: signer.name,
            order: index,
          })),
          ccEmailAddresses: signerEmails,
          uid: userId,
          fileName: fileName,
          fileData: firebaseUrl,
        }),
      });

      const result = await response.json();

      if (result.success) {
        await saveSignatureToFirestore(result);
        setInsightsModalOpen(true);

        toast.success("Document uploaded and signature saved successfully!");
      } else {
        console.error("API Error:", result);
        toast.error("Failed to create the embedded request. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error("Error uploading document. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async (fileName) => {
    try {
      const response = await fetch("https://api-seyimfnmxq-uc.a.run.app/getInsights", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uid: uid,
          fileName: fileName,
          text: location.state?.text || "",
        }),
      });
      const data = await response.json();
      // const data = {
      //   success: true,
      //   data: {
      //     keyTakeaways: [
      //       "This is a Non-Disclosure Agreement (NDA) between a Disclosing Party and a Receiving Party.",
      //       "Confidential Information is defined as information that has commercial value or utility in the business of the Disclosing Party.",
      //       "The Receiving Party's obligations do not extend to information that is publicly known, discovered or created by the Receiving Party before disclosure, learned through legitimate means, or disclosed with prior written approval.",
      //       "The Receiving Party must hold the Confidential Information in strict confidence and restrict access to authorized individuals.",
      //       "The nondisclosure provisions of this Agreement continue even after termination, until the Confidential Information no longer qualifies as a trade secret or until released by the Disclosing Party.",
      //       "The Agreement does not create a partnership, joint venture, or employment relationship between the parties.",
      //       "This Agreement supersedes all prior proposals, agreements, and understandings.",
      //       "Any provision found invalid or unenforceable will not affect the remaining provisions of the Agreement.",
      //       "The failure to exercise a right provided in the Agreement does not waive prior or subsequent rights.",
      //       "The Agreement provides notice of immunity for reporting suspected violations of law.",
      //       "The Agreement is binding on representatives, assigns, and successors of the parties.",
      //     ],
      //     riskyClauses: [],
      //     summary:
      //       "This document is a Non-Disclosure Agreement (NDA) between a Disclosing Party and a Receiving Party. It defines Confidential Information, sets obligations for the Receiving Party to maintain confidentiality, and outlines the exclusions from the Receiving Party's obligations. The Agreement continues even after termination, and it does not create a partnership or employment relationship. The Agreement supersedes prior agreements and provides notice of immunity for reporting suspected violations of law.",
      //   },
      // };

      setInsightsData(data.data);
      return data.data;
    } catch (error) {
      console.error("Error fetching insights:", error);
      toast.error("Failed to fetch insights.");
    }
  };

  const handleModalClose = () => {
    setInsightsModalOpen(false);
    setLoading(true);
    setTimeout(() => {
      history.push("/admin/dashboard");
      setLoading(false);
    }, 1000);
  };
  const handleSign = () => {
    setInsightsModalOpen(false);
    setLoading(true);
    setTimeout(() => {
      history.push("/admin/signature");
      setLoading(false);
    }, 2000);
  };

  useEffect(() => {
    if (data && auth) {
      setAddedSigners([{ name: displayName, email }]);
    }
  }, []);

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px", lg: "100px" }}>
      <Text fontSize="2xl" fontWeight="bold" color="gray.200" m={4}>
        Add Signers
      </Text>
      <Flex
        alignItems="center"
        wrap="wrap"
        mt={4}
        p={3}
        bg="white"
        borderRadius="8px"
        boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
      >
        <FormControl mt={3}>
          <FormLabel>
            <Text fontWeight="bold">Document Name</Text>
          </FormLabel>
          <Input
            borderColor={useColorModeValue("gray.300", "gray.600")}
            _hover={{ borderColor: "blue.500" }}
            _focus={{
              borderColor: "blue.500",
              // boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.9)",
            }}
            placeholder="Enter Document Name"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
          />
        </FormControl>
      </Flex>

      {addedSigners.map((signer, index) => (
        <Flex
          key={index}
          direction="row"
          justify="space-between"
          alignItems="center"
          wrap="wrap"
          mt={4}
          p={3}
          backgroundColor="rgba(255, 255, 255, 1)"
          borderRadius="8px"
          boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
        >
          <Text fontWeight="bold">{signer.name}</Text>
          <Text fontWeight="bold">{signer.email}</Text>
          <IconButton
            aria-label="Delete Signer"
            color="red.500"
            icon={<FaTrash />}
            bgColor="rgba(255, 255, 255, 1)"
            onClick={() => deleteSigner(index)}
          />
        </Flex>
      ))}

      <Box
        mt={6}
        p={4}
        backgroundColor="rgba(255, 255, 255, 0.9)"
        borderRadius="15px"
        boxShadow="0px 4px 10px rgba(0, 0, 0, 0.1)"
      >
        <Stack spacing={4}>
          <Flex direction={{ base: "column", md: "row" }} justify="space-between" wrap="wrap">
            <FormControl mt={3} flex={{ md: "1" }} pr={{ md: "2" }}>
              <FormLabel>
                <Text fontWeight="bold">Name</Text>
              </FormLabel>
              <Input
                borderColor={useColorModeValue("gray.300", "gray.600")}
                _hover={{ borderColor: "blue.500" }}
                _focus={{
                  borderColor: "blue.500",
                  boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.6)",
                }}
                placeholder="Signer Name"
                value={currentSigner.name}
                onChange={handleSignerInputChange("name")}
              />
            </FormControl>

            <FormControl mt={3} flex={{ md: "1" }} pl={{ md: "2" }}>
              <FormLabel>
                <Text fontWeight="bold">Email</Text>
              </FormLabel>
              <Input
                borderColor={useColorModeValue("gray.300", "gray.600")}
                _hover={{ borderColor: "blue.500" }}
                _focus={{
                  borderColor: "blue.500",
                  boxShadow: "0 0 0 3px rgba(66, 153, 225, 0.6)",
                }}
                placeholder="Signer Email"
                value={currentSigner.email}
                onChange={handleSignerInputChange("email")}
              />
            </FormControl>
          </Flex>

          <Button
            mt={4}
            alignSelf="flex-end"
            bgColor="black"
            color="white"
            size="md"
            onClick={addSigner}
            _hover={{ color: "black", bgColor: "gray.200" }}
            isDisabled={loading || buttonPressed}
          >
            Add Signer
          </Button>
        </Stack>
      </Box>
      <Flex justifyContent="space-between">
        <Button
          mt={4}
          alignSelf="flex-start"
          size="md"
          bgColor="gray.200"
          color="gray.900"
          _hover={{ color: "white", bgColor: "navy.400" }}
          onClick={handleBackClick}
          isDisabled={loading || buttonPressed}
        >
          Back
        </Button>

        <Button
          mt={4}
          alignSelf="flex-end"
          size="md"
          bgColor="navy.400"
          color="white"
          _hover={{ color: "black", bgColor: "gray.200" }}
          onClick={() => {
            setButtonPressed(true);
            handleNextClick();
          }}
          isLoading={loading}
          loadingText="Getting Insights..."
          isDisabled={!documentName || addedSigners.length === 0 || loading || buttonPressed}
        >
          Next
        </Button>
      </Flex>

      <Modal isOpen={isInsightsModalOpen} onClose={handleModalClose}>
        <ModalOverlay />
        <ModalContent
          p={5}
          fontFamily="Arial, sans-serif"
          maxW={useBreakpointValue({ base: "90%", md: "80%", lg: "70%" })}
          borderRadius="md"
          boxShadow="xl"
        >
          <ModalHeader fontSize="2xl" fontWeight="bold">
            Document Insights
          </ModalHeader>
          <ModalCloseButton />
          <Divider my={4} />

          <ModalBody>
            {/* Key Takeaways */}
            {insightsData.keyTakeaways && (
              <Box mb={4}>
                <Flex alignItems="center">
                  <Box as={FaInfoCircle} mr={2} color="blue.500" w={4} h={4} />
                  <Text fontWeight="bold" fontSize="lg">
                    Key Takeaways:
                  </Text>
                </Flex>
                <ul>
                  {insightsData.keyTakeaways.map((item, index) => (
                    <li key={index} style={{ marginBottom: "8px" }}>
                      {item}
                    </li>
                  ))}
                </ul>
              </Box>
            )}

            <Divider my={4} />

            {/* Risky Clauses */}
            {insightsData.riskyClauses && insightsData.riskyClauses.length > 0 ? (
              <Box mb={4}>
                <Flex alignItems="center">
                  <Box as={FaExclamationCircle} mr={2} color="red.500" w={4} h={4} />
                  <Text fontWeight="bold" fontSize="lg">
                    Risky Clauses:
                  </Text>
                </Flex>
                <ul>
                  {insightsData.riskyClauses.map((clause, index) => (
                    <li key={index} style={{ marginBottom: "8px" }}>
                      {clause}
                    </li>
                  ))}
                </ul>
              </Box>
            ) : (
              <Text>No risky clauses identified.</Text>
            )}

            <Divider my={4} />

            {/* Summary */}
            {insightsData.summary && (
              <Box>
                <Flex alignItems="center">
                  <Box as={FaBook} mr={2} w={4} h={4} />
                  <Text fontWeight="bold" fontSize="lg" mb={2}>
                    Summary:
                  </Text>
                </Flex>
                <Text>{insightsData.summary}</Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleModalClose}>
              Close
            </Button>
            <Button
              bgColor="navy.400"
              color="white"
              _hover={{ color: "black", bgColor: "gray.200" }}
              onClick={handleSign}
            >
              Sign Pending Docs
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={loading} isCentered>
        <ModalOverlay />
        <ModalContent
          backgroundColor="transparent"
          boxShadow="none"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Lottie animationData={addAnimation} style={{ width: 400, height: 400 }} />
        </ModalContent>
      </Modal>

      <ToastContainer />
    </Flex>
  );
}

export default AddSigner;
